import { AxiosInstance } from 'axios'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

export interface ChannelUnreadResponse {
  [channelId: string]: number
}

export interface ChannelUnreadCountResponse {
  channelId: string
  unreadCount: number
}

class ChannelUnreadService {
  private static instance: ChannelUnreadService
  private readonly api: AxiosInstance
  private cache: Map<string, { data: ChannelUnreadResponse | number; timestamp: number }> = new Map()
  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): ChannelUnreadService {
    if (!ChannelUnreadService.instance) {
      ChannelUnreadService.instance = new ChannelUnreadService(baseService)
    }
    return ChannelUnreadService.instance
  }

  /**
   * Get all unread counts for the current user
   */
  async getAllUnreadCounts(): Promise<ChannelUnreadResponse> {
    const cacheKey = 'all-unread-counts'
    const cached = this.getCachedData(cacheKey)

    if (cached && typeof cached === 'object') {
      return cached
    }

    const response = await this.api.get('/channel-unread/')
    this.setCachedData(cacheKey, response.data)
    return response.data
  }

  /**
   * Get unread count for a specific channel
   */
  async getUnreadCount(channelId: string): Promise<number> {
    const response = await this.api.get(`/channel-unread/${channelId}`)
    return response.data.unreadCount
  }

  /**
   * Increment unread count for a specific channel with a specific user
   */
  async notificationIncrementUnreadCountForUser({
    channelId,
    mentionerId,
    postId,
    receiverId,
  }: {
    channelId: string
    mentionerId: number
    postId: string
    receiverId: number
  }): Promise<ChannelUnreadCountResponse> {
    const response = await this.api.post(
      `/channel-unread/${channelId}/notification-increment/${receiverId}/${postId}`,
      {
        channelId,
        mentionerId,
        postId,
        receiverId,
      }
    )
    return response.data
  }

  /**
   * Get unread counts for multiple channels
   */
  async getBatchUnreadCounts(channelIds: string[]): Promise<ChannelUnreadResponse> {
    const response = await this.api.post('/channel-unread/batch', {
      channelIds,
    })
    return response.data
  }

  /**
   * Reset unread count for a specific channel
   */
  async resetUnreadCount(channelId: string): Promise<void> {
    await this.api.put(`/channel-unread/${channelId}/reset`)
    this.invalidateCache()
  }

  /**
   * Reset all unread counts
   */
  async resetAllUnreadCounts(): Promise<void> {
    await this.api.put('/channel-unread/reset-all')
    this.invalidateCache()
  }

  /**
   * Delete unread record for a specific channel
   */
  async deleteUnreadRecord(channelId: string): Promise<void> {
    await this.api.delete(`/channel-unread/${channelId}`)
    this.invalidateCache()
  }

  /**
   * Cache helper methods
   */
  private getCachedData(key: string): ChannelUnreadResponse | number | null {
    const cached = this.cache.get(key)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data
    }
    return null
  }

  private setCachedData(key: string, data: ChannelUnreadResponse | number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    })
  }

  private invalidateCache(): void {
    this.cache.clear()
  }
}

export const channelUnreadService = ChannelUnreadService.getInstance(baseService)
